/**
 * Overlay components (ui-ux §19.6–§19.8, §19.10): bottom sheets with snaps and drag-to-close, centred modals with a
 * folded corner, the confirm pattern (incl. the typed «СБРОС»), toasts with a manicule, tooltips (hover 300 ms on
 * desktop, long-press 400 ms on touch). All go through the OverlayManager. Owner: UI/Visual (F3).
 */
import { h, type Child } from '../dom';
import { glyph } from '../icons/shell.svg';
import { overlays, type OverlayManager } from '../overlay';
import { placeFloating } from '../kit/place';
import { button } from './controls';
import { haptic } from '../feedback/haptics';
import { LONG_PRESS_MS, TAP_SLOP_TOUCH } from '../input/constants';
import { ut } from '../strings';

let seq = 0;
const reduced = (): boolean => /^(reduced|min)$/.test(document.documentElement.dataset.motion ?? '');

export interface SheetHandle { el: HTMLDialogElement; open(): void; close(): Promise<void>; setSnap(s: 'peek' | 'half' | 'full'): void }

export interface SheetOpts {
  title?: Child;
  body: Child;
  actions?: Child;
  /** Declared snaps (1–3); the sheet opens at the last one. */
  snaps?: Array<'peek' | 'half' | 'full'>;
  /** Mandatory sheets refuse swipe / scrim / Esc closes. */
  mandatory?: boolean;
  desktop?: 'side' | 'center';
  onClose?: () => void;
  manager?: OverlayManager;
  /** Inline (docked, non-modal) sheet: part of the column, not a dialog in the top layer. */
  inline?: boolean;
  closeLabel?: string;
}

/** A bottom sheet (`<dialog class="mc-sheet">`): handle, title (lombard), body, sticky actions. */
export function sheet(o: SheetOpts): SheetHandle {
  const id = `sheet-${++seq}`;
  const mgr = o.manager ?? overlays;
  const snaps = o.snaps ?? [];
  const closeBtn = o.mandatory ? null : button({ variant: 'quill', icon: 'close', ariaLabel: o.closeLabel ?? ut('ui.kit.close'), onClick: () => void handle.close() });
  const handleBar = h('div', { class: 'mc-sheet__handle', attrs: { 'aria-hidden': 'true' } });
  const el = h('dialog', {
    class: `mc-sheet${o.inline ? ' mc-sheet--inline' : ''}`,
    attrs: { 'aria-labelledby': `${id}-t`, 'data-orn': 'deckle', ...(o.desktop ? { 'data-desktop': o.desktop } : {}), ...(snaps.length ? { 'data-snap': snaps[snaps.length - 1] } : {}) },
  },
    handleBar,
    o.title !== undefined || closeBtn ? h('div', { class: 'mc-sheet__head' }, h('h2', { class: 'mc-sheet__title', attrs: { id: `${id}-t`, tabindex: '-1' } }, o.title ?? ''), closeBtn) : null,
    h('div', { class: 'mc-sheet__body' }, o.body),
    o.actions ? h('div', { class: 'mc-sheet__actions' }, o.actions) : null) as HTMLDialogElement;

  el.addEventListener('cancel', (e) => { e.preventDefault(); if (!o.mandatory) void handle.close(); });
  // backdrop clicks land on the dialog element itself outside its box
  el.addEventListener('pointerdown', (e) => {
    if (e.target !== el || o.mandatory) return;
    const r = el.getBoundingClientRect();
    if (e.clientY < r.top || e.clientX < r.left || e.clientX > r.right) void handle.close();
  });

  // drag the handle down: > 25 % of the height or a flick > 0.5 px/ms steps down one snap / closes
  let startY = 0, startT = 0, dragging = false;
  handleBar.addEventListener('pointerdown', (e) => { dragging = true; startY = e.clientY; startT = e.timeStamp; handleBar.setPointerCapture(e.pointerId); el.classList.add('is-dragging'); });
  handleBar.addEventListener('pointermove', (e) => { if (dragging) el.style.setProperty('--drag-y', `${Math.max(0, e.clientY - startY)}px`); });
  const end = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    el.classList.remove('is-dragging');
    const dy = Math.max(0, e.clientY - startY), v = dy / Math.max(1, e.timeStamp - startT);
    el.style.removeProperty('--drag-y');
    if (dy > el.getBoundingClientRect().height * 0.25 || v > 0.5) {
      const cur = el.dataset.snap as 'peek' | 'half' | 'full' | undefined;
      const i = cur ? snaps.indexOf(cur) : -1;
      if (i > 0) { handle.setSnap(snaps[i - 1]); haptic('select'); } else if (!o.mandatory) void handle.close();
    }
  };
  handleBar.addEventListener('pointerup', end);
  handleBar.addEventListener('pointercancel', end);

  let opener: Element | null = null;
  const handle: SheetHandle = {
    el,
    open() {
      opener = document.activeElement;
      if (o.inline) { el.setAttribute('open', ''); return; }
      mgr.show({ id, kind: 'sheet', el, modal: true, onRequestClose: () => (o.mandatory ? false : (void handle.close(), undefined)) });
      requestAnimationFrame(() => (el.querySelector<HTMLElement>('.mc-sheet__body button, .mc-sheet__body [tabindex]') ?? el.querySelector<HTMLElement>('.mc-sheet__title'))?.focus({ preventScroll: true }));
    },
    close() {
      if (!el.open) return Promise.resolve();
      return new Promise((res) => {
        const done = (): void => { el.classList.remove('is-closing'); mgr.close(id); o.onClose?.(); (opener as HTMLElement | null)?.focus?.({ preventScroll: true }); res(); };
        if (reduced()) { done(); return; }
        el.classList.add('is-closing');
        el.addEventListener('animationend', done, { once: true });
        setTimeout(() => { if (el.classList.contains('is-closing')) done(); }, 260);
      });
    },
    setSnap(s) { el.dataset.snap = s; },
  };
  return handle;
}

export interface ModalHandle { el: HTMLDialogElement; open(): void; close(): void }

/** A centred modal page with a folded corner (art §9.2). */
export function modal(o: { title: Child; body: Child; actions?: Child; night?: boolean; mandatory?: boolean; onClose?: () => void; manager?: OverlayManager }): ModalHandle {
  const id = `modal-${++seq}`;
  const mgr = o.manager ?? overlays;
  const el = h('dialog', { class: `mc-modal${o.night ? ' mc-modal--night' : ''}`, attrs: { 'aria-labelledby': `${id}-t` } },
    o.mandatory ? null : h('div', { class: 'mc-modal__close' }, button({ variant: 'quill', icon: 'close', ariaLabel: ut('ui.kit.close'), onClick: () => handle.close() })),
    h('h2', { class: 'mc-modal__title', attrs: { id: `${id}-t` } }, o.title),
    h('div', null, o.body),
    o.actions ? h('div', { class: 'mc-btnrow', style: 'margin-top:16px' }, o.actions) : null) as HTMLDialogElement;
  el.addEventListener('cancel', (e) => { e.preventDefault(); if (!o.mandatory) handle.close(); });
  const handle: ModalHandle = {
    el,
    open() { mgr.show({ id, kind: 'modal', el, modal: true, onRequestClose: () => (o.mandatory ? false : undefined) }); },
    close() { mgr.close(id); o.onClose?.(); },
  };
  return handle;
}

/**
 * The confirm pattern (§19.10): a drop-cap title, a plain consequence line, [Отмена] (primary) + the verb as danger
 * ≥ 16 px apart. `typedWord` (e.g. «СБРОС») enables the danger button only on an exact match.
 */
export function confirmSheet(o: { title: string; consequence: string; verb: string; onConfirm: () => void; typedWord?: string; cancelLabel?: string; manager?: OverlayManager }): SheetHandle {
  const danger = button({ variant: 'danger', label: o.verb, disabled: !!o.typedWord, onClick: () => { void s.close(); o.onConfirm(); } });
  const cancel = button({ variant: 'primary', label: o.cancelLabel ?? ut('ui.kit.cancel'), haptic: 'confirm', onClick: () => void s.close() });
  const typed = o.typedWord ? h('label', { class: 'mc-field' },
    h('span', { class: 'mc-field__label' }, ut('ui.kit.typeToConfirm', { word: o.typedWord })),
    h('input', {
      class: 'mc-input', attrs: { type: 'text', autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false' },
      on: { input: (e) => { const ok = (e.target as HTMLInputElement).value.trim() === o.typedWord; if (ok) danger.removeAttribute('aria-disabled'); else danger.setAttribute('aria-disabled', 'true'); } },
    })) : null;
  const s = sheet({
    title: o.title, manager: o.manager,
    body: [h('p', { class: 'mc-read', style: 'margin:0' }, o.consequence), typed],
    actions: h('div', { class: 'mc-btnrow' }, danger, cancel),
  });
  return s;
}

// ── toasts ────────────────────────────────────────────────────────────────────────────────────────────────────────

const liveToasts: HTMLElement[] = [];

function restack(): void {
  let y = 0;
  for (const t of liveToasts) { t.style.setProperty('--stack-y', `${y}px`); y += t.getBoundingClientRect().height + 8; }
}

/** A marginal-note toast with a manicule (popover="manual"); `ms` 0 = sticky until closed. Errors use `kind: 'error'`. */
export function toast(text: Child, o: { kind?: 'info' | 'error'; ms?: number; action?: { label: string; onClick: () => void }; manager?: OverlayManager } = {}): { el: HTMLElement; close(): void } {
  const id = `toast-${++seq}`;
  const mgr = o.manager ?? overlays;
  const el = h('div', { class: `mc-toast${o.kind === 'error' ? ' mc-toast--error' : ''}`, attrs: { popover: 'manual', role: o.kind === 'error' ? 'alert' : 'status' } },
    h('i', { class: 'mc-toast__manicule', attrs: { 'aria-hidden': 'true' } }),
    h('span', { class: 'mc-toast__text' }, text),
    o.action ? button({ variant: 'text', label: o.action.label, onClick: () => { o.action!.onClick(); close(); } }) : h('span'));
  const close = (): void => {
    if (!el.isConnected) return;
    const fin = (): void => { mgr.close(id); el.remove(); const i = liveToasts.indexOf(el); if (i >= 0) liveToasts.splice(i, 1); restack(); };
    if (reduced()) { fin(); return; }
    el.classList.add('is-leaving');
    setTimeout(fin, 180);
  };
  mgr.show({ id, kind: 'toast', el, modal: false });
  liveToasts.push(el);
  requestAnimationFrame(restack);
  if (o.kind === 'error') haptic('error');
  const ms = o.ms ?? 3200;
  if (ms > 0) setTimeout(close, ms);
  el.addEventListener('click', (e) => { if ((e.target as Element).closest('button')) return; close(); });
  return { el, close };
}

// ── tooltips ──────────────────────────────────────────────────────────────────────────────────────────────────────

let openTip: { el: HTMLElement; id: string; anchor: Element } | null = null;

/** Shows a tooltip (title + ≤ 3 lines) next to `anchor`; closes the previous one. */
export function showTooltip(anchor: Element, content: { title?: string; text: Child }, o: { manager?: OverlayManager; prefer?: 'top' | 'bottom' } = {}): () => void {
  hideTooltip();
  const mgr = o.manager ?? overlays;
  const id = `tip-${++seq}`;
  const arrow = h('i', { class: 'mc-tip__arrow', attrs: { 'aria-hidden': 'true' } });
  const el = h('div', { class: 'mc-tip', attrs: { popover: 'manual', role: 'tooltip', id } },
    content.title ? h('b', { class: 'mc-tip__title' }, content.title) : null, h('span', null, content.text), arrow);
  mgr.show({ id, kind: 'tooltip', el, modal: false });
  const touch = matchMedia('(pointer: coarse)').matches;
  const place = (): void => {
    const a = anchor.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const p = placeFloating({ x: a.left, y: a.top, w: a.width, h: a.height }, { w: r.width, h: r.height },
      { w: innerWidth, h: innerHeight }, { prefer: o.prefer ?? (touch ? 'top' : 'bottom') });
    el.style.setProperty('--tip-x', `${p.x}px`);
    el.style.setProperty('--tip-y', `${p.y}px`);
    el.style.setProperty('--arrow-x', `${p.arrowX}px`);
    el.dataset.side = p.side;
  };
  place();
  anchor.setAttribute('aria-describedby', id);
  openTip = { el, id, anchor };
  addEventListener('scroll', hideTooltip, { once: true, capture: true });
  return hideTooltip;
}

export function hideTooltip(): void {
  if (!openTip) return;
  overlays.close(openTip.id);
  openTip.el.remove();
  openTip.anchor.removeAttribute('aria-describedby');
  openTip = null;
}

/** Wires hover (300 ms, fine pointers), focus and long-press (400 ms, touch) to a tooltip on `anchor`. */
export function attachTooltip(anchor: HTMLElement, content: () => { title?: string; text: Child }): () => void {
  let timer = 0, sx = 0, sy = 0, fired = false;
  const open = (): void => { showTooltip(anchor, content()); };
  const onEnter = (): void => { if (matchMedia('(hover: hover) and (pointer: fine)').matches) timer = window.setTimeout(open, 300); };
  const onLeave = (): void => { clearTimeout(timer); hideTooltip(); };
  const onDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') return;
    sx = e.clientX; sy = e.clientY; fired = false;
    timer = window.setTimeout(() => { fired = true; haptic('longpress'); open(); }, LONG_PRESS_MS);
  };
  const onMove = (e: PointerEvent): void => { if (Math.hypot(e.clientX - sx, e.clientY - sy) > TAP_SLOP_TOUCH) clearTimeout(timer); };
  const onUp = (e: PointerEvent): void => { clearTimeout(timer); if (fired) { e.preventDefault(); hideTooltip(); } };
  const onClick = (e: MouseEvent): void => { if (fired) { e.preventDefault(); e.stopPropagation(); fired = false; } };
  anchor.addEventListener('pointerenter', onEnter);
  anchor.addEventListener('pointerleave', onLeave);
  anchor.addEventListener('focus', open);
  anchor.addEventListener('blur', hideTooltip);
  anchor.addEventListener('pointerdown', onDown);
  anchor.addEventListener('pointermove', onMove);
  anchor.addEventListener('pointerup', onUp);
  anchor.addEventListener('click', onClick, true);
  anchor.addEventListener('contextmenu', (e) => { if (matchMedia('(pointer: coarse)').matches) e.preventDefault(); });
  return () => {
    anchor.removeEventListener('pointerenter', onEnter); anchor.removeEventListener('pointerleave', onLeave);
    anchor.removeEventListener('focus', open); anchor.removeEventListener('blur', hideTooltip);
    anchor.removeEventListener('pointerdown', onDown); anchor.removeEventListener('pointermove', onMove);
    anchor.removeEventListener('pointerup', onUp); anchor.removeEventListener('click', onClick, true);
  };
}

/** A shell-glyph «i» info button that opens a tooltip on tap (the touch path for every hover tooltip). */
export function infoButton(label: string, content: () => { title?: string; text: Child }): HTMLButtonElement {
  const b = button({ variant: 'quill', icon: glyph('info', { size: 20 }), ariaLabel: label, onClick: () => showTooltip(b, content()) });
  return b;
}
